import * as React from 'react';
import { useEffect, useRef } from 'react';

const COLORS = ['#4f8ef7', '#f7c948', '#f74f4f', '#4ff78e', '#f74fb8', '#a04ff7'];
const COUNT = 120;
const DURATION = 4000;

interface Particle {
  x: number; y: number;
  vx: number; vy: number;
  rot: number; vrot: number;
  w: number; h: number;
  color: string;
}

function makeParticles(W: number, H: number): Particle[] {
  return Array.from({ length: COUNT }, () => ({
    x: Math.random() * W,
    y: -10 - Math.random() * H * 0.3,
    vx: (Math.random() - 0.5) * 3,
    vy: 2 + Math.random() * 4,
    rot: Math.random() * Math.PI * 2,
    vrot: (Math.random() - 0.5) * 0.2,
    w: 8 + Math.random() * 6,
    h: 4 + Math.random() * 4,
    color: COLORS[Math.floor(Math.random() * COLORS.length)],
  }));
}

export function Confetti() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;
    const particles = makeParticles(W, H);
    const start = performance.now();
    let rafId: number;

    function draw(now: number) {
      const elapsed = now - start;
      ctx.clearRect(0, 0, W, H);
      const alpha = elapsed > DURATION - 800
        ? Math.max(0, 1 - (elapsed - (DURATION - 800)) / 800)
        : 1;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.rot += p.vrot;
        ctx.save();
        ctx.globalAlpha = alpha;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (elapsed < DURATION) {
        rafId = requestAnimationFrame(draw);
      } else {
        ctx.clearRect(0, 0, W, H);
      }
    }

    rafId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(rafId);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed', top: 0, left: 0,
        width: '100vw', height: '100vh',
        pointerEvents: 'none', zIndex: 9999,
      }}
    />
  );
}
